/**
 * Computes layouts for graphs with ELK js and caches them.
 */
import { graphSelector, setRuntime } from './runtimeSlice';
import { visibleElementsSelector, visibleNodesSelector } from './selectionSlice';
import { toELKGraph } from './utils/toELKGraph';
import ELK from 'elkjs/lib/elk-api';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { createSliceSelector } from './utils/createSliceSelector';
import { useAppSelector } from '../../hooks';
import { useDispatch } from 'react-redux';
import * as React from 'react';
import { ELKToPositions } from './utils/ELKToPositions';
const initialState = {};
// We copy the ELK worker file to the webpack build as is, from the ELK package, so we can load it as a webworker.
const elkWorker = new Worker('./elk-worker.js');
const elk = new ELK({
    workerFactory: () => elkWorker,
});
const computeLayout = createAsyncThunk('computeLayout', (_, { getState }) => elk.layout(elkGraphSelector(getState())), {
    // Add key to pending metadata
    getPendingMeta: ({ requestId }, { getState }) => ({ requestId, key: currentLayoutKeySelector(getState()) }),
    // Only run if we can't find a layout already for this key
    condition: (_node, { getState }) => !hasLayoutSelector(getState()),
});
/**
 * Try recomputing layout, when either runtime or selections change
 */
export function useRecomputeLayout() {
    const dispatch = useDispatch();
    const layoutKey = useAppSelector(currentLayoutKeySelector);
    // Recompute layout whenever any key for computing it changes
    React.useEffect(() => {
        dispatch(computeLayout());
    }, [layoutKey]);
}
export const layoutSlice = createSlice({
    name: 'layout',
    initialState,
    reducers: {},
    // Avoid returning builder to prevent cyclical types
    // https://github.com/reduxjs/redux-toolkit/issues/324#issuecomment-615391051
    extraReducers: (builder) => {
        builder
            .addCase(setRuntime, () => initialState)
            .addCase(computeLayout.pending, (state, action) => {
            state[action.meta.requestId] = {
                value: { type: 'loading' },
                key: action.meta.key,
            };
        })
            .addCase(computeLayout.fulfilled, (state, action) => {
            state[action.meta.requestId].value = { positions: ELKToPositions(action.payload), type: 'done' };
        })
            .addCase(computeLayout.rejected, (state, action) => {
            state[action.meta.requestId].value = { type: 'error', error: action.error };
        });
    },
});
const layoutSelector = createSliceSelector(layoutSlice);
const elkGraphSelector = createSelector(graphSelector, visibleElementsSelector, (graph, visible) => graph === null ? null : toELKGraph(graph, visible));
function idsToString(ids) {
    return [...ids].sort().join(',');
}
const visibleNodesStringSelector = createSelector(visibleNodesSelector, (nodes) => nodes === null ? null : idsToString(nodes));
const currentLayoutKeySelector = createSelector(graphSelector, visibleNodesStringSelector, (graph, visibleNodesString) => ({
    graph,
    visibleNodesString,
}));
const currentLayoutStatusSelector = createSelector(layoutSelector, currentLayoutKeySelector, (layout, { graph, visibleNodesString }) => { var _a; 
// Compare graph by identity, to speed up comparison
return (_a = Object.values(layout).find(({ key }) => graph === key.graph && visibleNodesString === key.visibleNodesString)) !== null && _a !== void 0 ? _a : null; });
const hasLayoutSelector = createSelector(currentLayoutStatusSelector, (status) => status !== null);
export const currentLayoutSelector = createSelector(currentLayoutStatusSelector, (status) => { var _a; return (_a = status === null || status === void 0 ? void 0 : status.value) !== null && _a !== void 0 ? _a : null; });
export const currentPositionsSelector = createSelector(currentLayoutSelector, (value) => (value === null || value === void 0 ? void 0 : value.type) === 'done' ? value.positions : null);
